import React, {useState, useEffect} from "react";
import {useSearchParams} from "react-router-dom";
import { useVsiAppContext} from "../../../../stores/VsiApp";
import { getReport, postReport } from "../../../../utils/Verbs";
import {Card, Spinner, Table} from "reactstrap";
import Toggle from "react-toggle";
import { VsiBubble, VsiH4, VsiLgText } from "../../../../styled/App.styled";
import themeStyles from "../../../../utils/themeStyles";
import Grid from "../../../../components/Grid";
import {deviceHealthFormatter, healthColorAlertFormatter, removeURIEncode} from "../../../../utils/gridFormatters";
import {useTitleFromComponent} from "../../../../utils/hooks";
import AcknowledgeButton from "../../../../components/AcknowledgeButton";
import VsiAlert from "../../../../components/VsiAlert";
import { VsiSelectLabel } from "../../../../styled/VsiLabeledSelect.styled";


const alertsDefs = [
    {
        field: 'severity',
        headerName: 'Severity',
        cellRenderer: healthColorAlertFormatter,
        "filter": "agSetColumnFilter"
    },
    {
        field: 'device_name',
        headerName: 'Device Name',
    },
    {
        field: 'category',
        headerName: 'Category',
    },
    {
        field: 'alert',
        headerName: 'Alert',
        cellRenderer: removeURIEncode,
    },
    {
        field: 'description',
        headerName: 'Description',
        cellRenderer: removeURIEncode,
    },
];

const healthFields = [
    {
        fieldValue: 'config_alert_level',
        fieldText: 'config_alert_text',
        title: 'Configuration & Setup',
    },
    {
        fieldValue: 'balance_alert_level',
        fieldText: 'balance_alert_text',
        title: 'Balance',
    },
    {
        fieldValue: 'host_alert_level',
        fieldText: 'host_alert_text',
        title: 'Server Volume Mapping',
    },
    {
        fieldValue: 'general_errors_alert_level',
        fieldText: 'general_errors_alert_text',
        title: 'Errors & Other Alerts',
    },
    {
        fieldValue: 'pool_alert_level',
        fieldText: 'pool_alert_text',
        title: 'Storage Layout',
    },
    {
        fieldValue: 'volume_snap_alert_level',
        fieldText: 'volume_snap_alert_text',
        title: 'Volumes/Snapshots',
    }
]

export default function DeviceHealth({title}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const deviceName = decodeURIComponent(searchParams.get('device_name'));
    useTitleFromComponent(`${deviceName} / ${title}`);

    const [healthData, setHealthData] = useState([]);
    const [acknowledgedData, setAcknowledgedData] = useState([]);
    const [unacknowledgedData, setUnacknowledgedData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAcknowledging, setIsAcknowledging] = useState(false);
    const [isAcknowledged, setIsAcknowledged] = useState(false);
    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

    const [selectedAlerts, setSelectedAlerts] = useState([]);

    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);

    const getHealth = async function () {
        const device_name = searchParams.get('device_name');
        const token = await getAccessTokenSilently();
        const route = `/api/v6/report/device_health_proc?client_id=${client.clientId}&period_id=${periodId}&device_name=${device_name}`;
        try {
            const data = await getReport(route, null, token);
            setHealthData(data?.data?.recordset?.[0] || []);
            setAcknowledgedData(data?.data?.recordsets?.[1].filter(alert => alert.acknowledged) || []);
            setUnacknowledgedData(data?.data?.recordsets?.[1].filter(alert => !alert.acknowledged) || []);
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
        }
    };

    const onAcknowledge = async () => {
        if (selectedAlerts.length < 1 || isAcknowledging) return;
        let selectedRows = [...selectedAlerts];
        // Acknowledged > Not
        const route = `/api/v6/report/batch/acknowledge_alerts`;
        const updatedAlerts = {};
        const allAlerts = [];

        for (let selectedRow of selectedRows) {
            const alert = {
                acknowledged: (!selectedRow.acknowledged).toString(),
                context: 'device',
                description: encodeURIComponent(selectedRow.description),
                alert: encodeURIComponent(selectedRow.alert),
                client_id: client.clientId,
                device_name: encodeURIComponent(deviceName),
                category: encodeURIComponent(selectedRow.category),
                severity: selectedRow.severity,
                user: user.email
            }

            allAlerts.push(alert);
            updatedAlerts[selectedRow.id] = !selectedRow.acknowledged;
        }
        setIsAcknowledging(true);
        try {
            const token = await getAccessTokenSilently();
            await postReport(route, allAlerts, token);

            const newAcknowledgedData = [...acknowledgedData, ...unacknowledgedData].filter(alert => {
                return alert.acknowledged && !updatedAlerts.hasOwnProperty(alert.id);
            });
            const newUnacknowledgedData = [...acknowledgedData, ...unacknowledgedData].filter(alert => {
                return !alert.acknowledged && !updatedAlerts.hasOwnProperty(alert.id);
            });
            selectedRows.forEach(row => {
                if (updatedAlerts.hasOwnProperty(row.id)) {
                    const ack = updatedAlerts[row.id];
                    const newAlert = {
                        ...row,
                        acknowledged: ack,
                    };

                    ack ? newAcknowledgedData.unshift(newAlert) : newUnacknowledgedData.unshift(newAlert);
                }
            });

            setAcknowledgedData(newAcknowledgedData);
            setUnacknowledgedData(newUnacknowledgedData);
            setSelectedAlerts([]);
            setIsAcknowledging(false);
            setIsError(false);
            setMessage('Alerts updated!')
        } catch (e) {
            console.error(e);
            setIsError(true);
            setMessage(e.message);
            setIsAcknowledging(false);
        }
    };

    const onRowSelected = (e) => {
        const alerts = [...(e?.api?.getSelectedNodes?.() ?? [])];
        setSelectedAlerts(alerts.map(({data}) => data));
    }

    const onToggle = () => {
        searchParams.set('type', !isAcknowledged ? "acknowledged" : "unacknowledged");
        setSearchParams(searchParams, {replace: true});
        setIsAcknowledged(!isAcknowledged);
    }


    useEffect(() => {
        getHealth();
    }, [client, searchParams.get("period_id")]);

    useEffect(() => {
        let timeout = null;
        if (message) {
            timeout = setTimeout(() => {
                setMessage('');
            }, 3500);
        }
        return () => clearTimeout(timeout);
    }, [message]);
    return (
      <>
          <VsiAlert isOpen={Boolean(message)} color={isError ? 'danger' : 'success'} toggle={() => setMessage('')}>
              {message}
          </VsiAlert>
          <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Device Health - {searchParams.get('device_name')}</VsiH4>
            <VsiBubble autobotName='SummaryTable' autobotInfo={{
                data: healthData,
                config: healthFields
            }}>
                <Table className={"mb-4"}>
                    <thead>
                    <tr>
                        {healthFields.map(({fieldValue, title}, index) => {
                            return <th align="center" style={{textAlign: 'center', color: themeStyles[theme].textColor}}
                                       key={`health-title-${index}-${fieldValue}`}>{title}</th>
                        })}
                    </tr>

                    </thead>
                    <tbody>
                    <tr>
                        {healthFields.map(({fieldValue}, index) => {
                            return <td align="center" key={`health-field-${index}-${fieldValue}`}>{deviceHealthFormatter({
                                value: healthData[fieldValue]
                            })}</td>
                        })}
                    </tr>
                    </tbody>
                </Table>
               <VsiLgText>
                    {`You can ${isAcknowledged ? 'un' : ''}acknowledge (${isAcknowledged ? 'show' : 'hide'}) alerts. Use Shift + Click to select multiple alerts at once`}
                </VsiLgText>
                <div className="d-flex flex-row align-items-start justify-content-between">
                  <div className="d-flex flex-row align-items-center justify-content-start">
                    <div className={"me-4"}>
                         <VsiSelectLabel>Show Acknowledged?</VsiSelectLabel>
                          <div className="d-flex align-items-center justify-content-around">
                             <Toggle checked={isAcknowledged}
                                     onChange={onToggle}
                                     disabled={isLoading || isAcknowledging}/>
                          </div>
                    </div>
                    <div className={"d-flex flex-row justify-content-end"}>
                        <AcknowledgeButton allowAcknowledge={true}
                                           isLoading={isAcknowledging || isLoading}
                                           acknowledgedToggle={isAcknowledged}
                                           onAcknowledge={onAcknowledge}
                                           disable={(isAcknowledging || isLoading) || selectedAlerts.length < 1}/>
                            </div>
                  </div>
                </div>
            </VsiBubble>
            {isLoading ? <Spinner color={"primary"}/> : (
                <VsiBubble>
                    <Grid
                        data={isAcknowledged ? acknowledgedData : unacknowledgedData}
                       isLoading={isAcknowledging || isLoading}
                        rowSelection={"multiple"}
                        gridText={`All ${!isAcknowledged ? 'un' : ''}acknowledged alerts`}
                        columnDefs={alertsDefs}
                        onRowSelected={onRowSelected} />
                </VsiBubble>
            )}
        </Card>
      </>
    )
}
